import { useState } from "react";
import { supabase } from "@/lib/supabaseClient";

interface Props {
  tenantId: string;
  clientId: string;
  tenantName: string;
  onActivated: () => void;
}

type Step = "choice" | "request" | "promo" | "success";

export default function ActivationModal({ tenantId, clientId, tenantName, onActivated }: Props) {
  const [step, setStep]         = useState<Step>("choice");
  const [promoCode, setPromoCode] = useState("");
  const [promoError, setPromoError] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);
  const [requestSent, setRequestSent]   = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);

  /* ── Promo code validation ── */
  const handlePromoSubmit = async () => {
    if (!promoCode.trim()) { setPromoError("Ingresa un código"); return; }
    setPromoLoading(true);
    setPromoError("");

    // Check if code exists in client_addons with source='promo_code' for this tenant
    const { data: addonRow } = await supabase
      .from("client_addons")
      .select("id, status, notes")
      .eq("tenant_id", tenantId)
      .eq("client_id", clientId)
      .eq("addon_key", "digital_invitation")
      .maybeSingle();

    // Simple guard: if notes contains the promo code → activate
    // In a real flow, you'd have a promo_codes table. For now we activate if the code is stored in notes
    if (addonRow && addonRow.notes?.includes(promoCode.toUpperCase())) {
      // Activate
      await supabase
        .from("client_addons")
        .update({ status: "active", updated_at: new Date().toISOString() })
        .eq("id", addonRow.id);
      setPromoLoading(false);
      setStep("success");
      setTimeout(onActivated, 1800);
      return;
    }

    // Create new addon row with promo code (photographer activates later)
    if (!addonRow) {
      await supabase.from("client_addons").insert({
        tenant_id:   tenantId,
        client_id:   clientId,
        addon_key:   "digital_invitation",
        status:      "pending",
        price:       0,
        source:      "promo_code",
        notes:       `Promo solicitado: ${promoCode.toUpperCase()}`,
      });
    }
    setPromoLoading(false);
    setPromoError("Código inválido o pendiente de activación. Tu fotógrafo recibirá una notificación.");
  };

  /* ── Request activation ── */
  const handleRequestActivation = async () => {
    setRequestLoading(true);
    // Check if already requested
    const { data: existing } = await supabase
      .from("client_addons")
      .select("id")
      .eq("tenant_id", tenantId)
      .eq("client_id", clientId)
      .eq("addon_key", "digital_invitation")
      .maybeSingle();

    if (!existing) {
      await supabase.from("client_addons").insert({
        tenant_id: tenantId,
        client_id: clientId,
        addon_key: "digital_invitation",
        status:    "pending",
        price:     0,
        source:    "client_request",
        notes:     "Cliente solicitó activación desde portal",
      });
      // Notify admin
      await supabase.from("notifications").insert({
        tenant_id: tenantId,
        type:      "info",
        title:     "Solicitud de Invitación Digital",
        message:   "Un cliente solicitó activar la invitación digital desde su portal.",
      });
    }
    setRequestLoading(false);
    setRequestSent(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/30 flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        {/* Card */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          {/* Header visual */}
          <div className="relative h-36 bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="absolute w-24 h-24 rounded-full border border-white/20"
                  style={{ top: `${20 + i * 10}%`, left: `${10 + i * 15}%`, transform: "translate(-50%,-50%)" }} />
              ))}
            </div>
            <div className="relative text-center">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-white/10 flex items-center justify-center mb-2">
                <i className="ri-mail-send-line text-white text-3xl"></i>
              </div>
            </div>
          </div>

          <div className="p-6 md:p-8">
            {/* ── CHOICE ── */}
            {step === "choice" && (
              <div className="space-y-5">
                <div className="text-center">
                  <h1 className="text-[#0f172a] text-xl font-bold mb-2">Invitación Digital</h1>
                  <p className="text-slate-500 text-sm leading-relaxed">
                    Crea tu invitación digital personalizada. Comparte el enlace con tus invitados y gestiona las confirmaciones.
                  </p>
                </div>

                <div className="bg-slate-50 rounded-xl p-4 space-y-2">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">¿Qué incluye?</p>
                  {[
                    "Diseño personalizado con tu foto",
                    "Música de fondo",
                    "Gestión de invitados y RSVPs",
                    "Link y código QR para compartir",
                    "Detalles de ceremonia y recepción",
                  ].map((f) => (
                    <div key={f} className="flex items-center gap-2 text-sm text-slate-600">
                      <span className="w-4 h-4 flex items-center justify-center text-emerald-500 shrink-0">
                        <i className="ri-checkbox-circle-fill text-sm"></i>
                      </span>
                      {f}
                    </div>
                  ))}
                </div>

                <div className="space-y-3">
                  <button
                    onClick={() => setStep("promo")}
                    className="w-full flex items-center justify-between px-4 py-3.5 bg-[#C8A96E] hover:bg-[#b8965a] text-white rounded-xl font-medium text-sm transition-all cursor-pointer"
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-lg">
                        <i className="ri-coupon-3-line"></i>
                      </span>
                      <div className="text-left">
                        <p className="font-semibold">Tengo un código promocional</p>
                        <p className="text-white/70 text-xs">Ingresar código y activar</p>
                      </div>
                    </div>
                    <i className="ri-arrow-right-line"></i>
                  </button>

                  <button
                    onClick={() => setStep("request")}
                    className="w-full flex items-center justify-between px-4 py-3.5 bg-slate-50 hover:bg-slate-100 text-slate-700 border border-slate-200 rounded-xl font-medium text-sm transition-all cursor-pointer"
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 flex items-center justify-center bg-slate-200 rounded-lg">
                        <i className="ri-customer-service-2-line text-slate-500"></i>
                      </span>
                      <div className="text-left">
                        <p className="font-semibold">Solicitar a mi fotógrafo</p>
                        <p className="text-slate-400 text-xs">Tu fotógrafo recibirá la solicitud</p>
                      </div>
                    </div>
                    <i className="ri-arrow-right-line text-slate-400"></i>
                  </button>
                </div>

                <p className="text-center text-xs text-slate-400">
                  Estudio: <span className="font-medium text-slate-600">{tenantName}</span>
                </p>
              </div>
            )}

            {/* ── PROMO CODE ── */}
            {step === "promo" && (
              <div className="space-y-5">
                <button onClick={() => setStep("choice")} className="flex items-center gap-1.5 text-slate-400 hover:text-slate-600 text-sm transition-colors cursor-pointer">
                  <i className="ri-arrow-left-line"></i> Volver
                </button>
                <div className="text-center">
                  <div className="w-12 h-12 mx-auto rounded-xl bg-[#C8A96E]/10 flex items-center justify-center mb-3">
                    <i className="ri-coupon-3-line text-[#C8A96E] text-xl"></i>
                  </div>
                  <h2 className="text-[#0f172a] font-bold text-lg">Código Promocional</h2>
                  <p className="text-slate-500 text-sm mt-1">Tu fotógrafo te proporcionó un código único.</p>
                </div>
                <div>
                  <input
                    type="text"
                    value={promoCode}
                    onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoError(""); }}
                    placeholder="Ej: BODA2025"
                    className="w-full border border-slate-200 rounded-xl px-4 py-3 text-center text-lg font-bold tracking-widest text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 transition-colors font-mono uppercase"
                    onKeyDown={(e) => e.key === "Enter" && handlePromoSubmit()}
                    maxLength={20}
                  />
                  {promoError && (
                    <p className="text-xs text-amber-600 mt-2 flex items-center gap-1.5 bg-amber-50 rounded-lg px-3 py-2">
                      <i className="ri-information-line shrink-0"></i>
                      {promoError}
                    </p>
                  )}
                </div>
                <button
                  onClick={handlePromoSubmit}
                  disabled={promoLoading || !promoCode.trim()}
                  className="w-full flex items-center justify-center gap-2 bg-[#C8A96E] hover:bg-[#b8965a] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-xl transition-all cursor-pointer text-sm"
                >
                  {promoLoading ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
                  {promoLoading ? "Verificando…" : "Activar con Código"}
                </button>
              </div>
            )}

            {/* ── REQUEST ── */}
            {step === "request" && (
              <div className="space-y-5">
                {!requestSent ? (
                  <>
                    <button onClick={() => setStep("choice")} className="flex items-center gap-1.5 text-slate-400 hover:text-slate-600 text-sm transition-colors cursor-pointer">
                      <i className="ri-arrow-left-line"></i> Volver
                    </button>
                    <div className="text-center">
                      <div className="w-12 h-12 mx-auto rounded-xl bg-slate-100 flex items-center justify-center mb-3">
                        <i className="ri-customer-service-2-line text-slate-500 text-xl"></i>
                      </div>
                      <h2 className="text-[#0f172a] font-bold text-lg">Solicitar Activación</h2>
                      <p className="text-slate-500 text-sm mt-1">
                        Se enviará una notificación a <span className="font-medium text-slate-700">{tenantName}</span> para que activen tu invitación digital.
                      </p>
                    </div>
                    <div className="bg-blue-50 rounded-xl p-4 text-sm text-blue-700">
                      <p className="font-medium mb-1">¿Qué sucede al solicitar?</p>
                      <ul className="space-y-1 text-xs text-blue-600">
                        <li className="flex items-center gap-1.5"><i className="ri-checkbox-blank-circle-line text-[8px]"></i>Tu fotógrafo recibe la solicitud</li>
                        <li className="flex items-center gap-1.5"><i className="ri-checkbox-blank-circle-line text-[8px]"></i>Ellos confirmarán la activación</li>
                        <li className="flex items-center gap-1.5"><i className="ri-checkbox-blank-circle-line text-[8px]"></i>Una vez activo, podrás acceder desde aquí</li>
                      </ul>
                    </div>
                    <button
                      onClick={handleRequestActivation}
                      disabled={requestLoading}
                      className="w-full flex items-center justify-center gap-2 bg-[#0f172a] hover:bg-slate-800 disabled:opacity-50 text-white font-semibold py-3 rounded-xl transition-all cursor-pointer text-sm"
                    >
                      {requestLoading ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-send-plane-line"></i>}
                      {requestLoading ? "Enviando…" : "Enviar Solicitud"}
                    </button>
                  </>
                ) : (
                  <div className="text-center space-y-4 py-4">
                    <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
                      <i className="ri-checkbox-circle-line text-emerald-600 text-3xl"></i>
                    </div>
                    <div>
                      <h2 className="text-[#0f172a] font-bold text-lg">¡Solicitud enviada!</h2>
                      <p className="text-slate-500 text-sm mt-2">
                        <span className="font-medium text-slate-700">{tenantName}</span> ha recibido tu solicitud. Una vez activada, regresa a esta página para crear tu invitación.
                      </p>
                    </div>
                    <a href=".." className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 border border-slate-200 rounded-xl px-4 py-2.5 transition-colors cursor-pointer">
                      <i className="ri-arrow-left-line"></i> Volver al portal
                    </a>
                  </div>
                )}
              </div>
            )}

            {/* ── SUCCESS ── */}
            {step === "success" && (
              <div className="text-center space-y-4 py-4">
                <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
                  <i className="ri-checkbox-circle-fill text-emerald-600 text-3xl"></i>
                </div>
                <div>
                  <h2 className="text-[#0f172a] font-bold text-lg">¡Activada!</h2>
                  <p className="text-slate-500 text-sm mt-2">Tu invitación digital ha sido activada. Cargando editor…</p>
                </div>
                <div className="flex items-center justify-center gap-2 text-[#C8A96E] text-sm">
                  <i className="ri-loader-4-line animate-spin"></i>
                  <span>Un momento…</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}